import { Check, Minus } from 'lucide-react'
import { NAVIGATOR_LABELS, NAVIGATOR_STATUS_LABELS } from './labels'
import type { NavigatorStatus } from './types'

// Shared marker for the chart's "Navigator users" view. The meaning is carried
// by the shape (check vs. dash) and the fill (solid green vs. outlined white),
// never by hue alone — the owner avatars underneath already use colour to say
// internal vs. external.
const MARKER_CLASSES: Record<'yes' | 'no', string> = {
  yes: 'bg-status-on-track text-white',
  no: 'bg-white text-charcoal border border-charcoal',
}

export function isNavigatorUser(status: NavigatorStatus): boolean {
  return status === 'active'
}

/** Corner marker for the 24px initial avatars on a role box. */
export function NavigatorAvatarMarker({ status, name }: { status: NavigatorStatus; name: string }) {
  const yes = isNavigatorUser(status)
  const label = `${name} — ${NAVIGATOR_STATUS_LABELS[status]}`

  return (
    <span
      title={label}
      data-testid="navigator-avatar-marker"
      data-navigator-status={status}
      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white flex items-center justify-center ${MARKER_CLASSES[yes ? 'yes' : 'no']}`}
    >
      {yes
        ? <Check size={8} strokeWidth={4} aria-hidden="true" />
        : <Minus size={8} strokeWidth={4} aria-hidden="true" />}
      <span className="sr-only">{label}</span>
    </span>
  )
}

/** Inline pill for owner lists where the full name is visible. */
export function NavigatorBadge({ status }: { status: NavigatorStatus }) {
  const yes = isNavigatorUser(status)

  return (
    <span
      title={NAVIGATOR_STATUS_LABELS[status]}
      data-testid="navigator-badge"
      data-navigator-status={status}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-badge text-[10px] font-semibold whitespace-nowrap ${yes ? 'bg-status-on-track-bg text-status-on-track' : 'bg-stone text-text-primary'}`}
    >
      {yes
        ? <Check size={10} strokeWidth={3} aria-hidden="true" />
        : <Minus size={10} strokeWidth={3} aria-hidden="true" />}
      {yes ? NAVIGATOR_LABELS.badgeYes : NAVIGATOR_LABELS.badgeNo}
    </span>
  )
}

/** Key shown beside the summary so the two markers can't be misread. */
export function NavigatorLegend() {
  return (
    <span className="inline-flex items-center gap-3 text-xs text-text-secondary">
      <span className="inline-flex items-center gap-1">
        <span className={`w-3 h-3 rounded-full flex items-center justify-center ${MARKER_CLASSES.yes}`}>
          <Check size={8} strokeWidth={4} aria-hidden="true" />
        </span>
        {NAVIGATOR_LABELS.legendYes}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={`w-3 h-3 rounded-full flex items-center justify-center ${MARKER_CLASSES.no}`}>
          <Minus size={8} strokeWidth={4} aria-hidden="true" />
        </span>
        {NAVIGATOR_LABELS.legendNo}
      </span>
    </span>
  )
}
